import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	RecentlyUpdatedMacroQuery,
	type RecentlyUpdatedMacroQuery$data,
	type RecentlyUpdatedMacroQuery$variables,
} from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroQuery.ts';
import { buildRecentlyUpdatedQueryVariables } from '@confluence/recently-updated-macro/entry-points/recentlyUpdatedQueryUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

const buildVariables = (
	node: MacroContentNode,
	context: MacroPreloadContext,
): RecentlyUpdatedMacroQuery$variables => {
	const macroParams = node.attrs?.parameters?.macroParams ?? {};

	const params = {
		types: macroParams['types']?.value,
		spaces: macroParams['spaces']?.value,
		labels: macroParams['labels']?.value,
		author: macroParams['author']?.value,
		max: Number(macroParams['max']?.value) || undefined,
	};
	const spaceKey = params.spaces ?? context.spaceKey ?? '';

	return buildRecentlyUpdatedQueryVariables(params, spaceKey);
};

/**
 * Preload handler for the recently-updated macro.
 *
 * For a recently-updated extension node found in the ADF content, this handler
 * builds query variables from the macro parameters and fires the
 * RecentlyUpdatedMacroQuery GraphQL query to populate the Relay store.
 */
export const recentlyUpdatedPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'recently-updated',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const variables = buildVariables(node, context);
		await query<RecentlyUpdatedMacroQuery$data, RecentlyUpdatedMacroQuery$variables>({
			query: RecentlyUpdatedMacroQuery,
			variables,
			context: { single: true },
		});
	},
};
